import type { CmsRendering } from '@eneco/cms-adapter';

/**
 * Resolve a logical slot ("the left column of the hero") to the placeholder name the *current
 * provider* actually used.
 *
 * Sitecore names slots `jss-hero-left`; a Sanity block names them after its array field. Hard-coding
 * either makes a component render blank on the other provider — the exact class of difference the
 * `?_cms=sitecore` / `?_cms=sanity` comparison exists to surface, so components resolve names instead.
 *
 * Returns the first candidate that is present **and non-empty** on the rendering, or `undefined` when
 * the provider gave this block no such slot (the normal case for the migration bundle today, which has
 * no component-level placeholder fields at all: a container's children are entries of the page's flat
 * `sections` list, carrying `parentKey` and `area`, ADR-0068).
 */
export const resolveSlot = (rendering: CmsRendering | undefined, candidates: readonly string[]): string | undefined =>
  candidates.find(candidate => (rendering?.placeholders?.[candidate]?.length ?? 0) > 0);

/** Slot-name candidates per logical hero slot, Sitecore name first. */
export const HERO_SLOTS = {
  promo: ['jss-promo-box', 'promoBox', 'promo'],
  left: ['jss-hero-left', 'heroLeft', 'left'],
  right: ['jss-hero-right', 'heroRight', 'right'],
  bottom: ['jss-hero-bottom', 'heroBottom', 'bottom'],
} as const;

/** `Section`'s one nested placeholder — `libs/cms-sanity-schema/src/sections/Section.ts`'s `sectionMain`. */
export const SECTION_SLOTS = {
  main: ['jss-section-main', 'sectionMain'],
} as const;

/**
 * `SplitView`'s three nested placeholders — `libs/cms-sanity-schema/src/sections/SplitView.ts`'s
 * `splitViewFirst`/`splitViewSecond`/`promoBox`, all present 1:1 with the Sitecore names.
 */
export const SPLIT_VIEW_SLOTS = {
  first: ['jss-split-view-first', 'splitViewFirst'],
  second: ['jss-split-view-second', 'splitViewSecond'],
  promo: ['jss-promo-box', 'promoBox'],
} as const;

/** `TitleTextCTA`'s one nested placeholder — matches `libs/cms-sanity-schema/src/sections/TitleTextCTA.ts`'s `titleTextCtaMain`. */
export const TITLE_TEXT_CTA_SLOTS = {
  main: ['jss-title-text-cta-main', 'titleTextCtaMain'],
} as const;

/**
 * `HeroCard`'s nested placeholders. Only `jss-hero-card-main`/`heroCardMain` exists in the migration
 * bundle (`libs/cms-sanity-schema/src/sections/HeroCard.ts`) — Sitecore's `jss-hero-card-top` has no
 * Sanity-side field at all, so that candidate list has no second entry and always resolves empty there.
 */
export const HERO_CARD_SLOTS = {
  top: ['jss-hero-card-top'],
  main: ['jss-hero-card-main', 'heroCardMain'],
} as const;

/**
 * `LineUp`'s nested placeholders. Only `jss-line-up-main`/`lineUpMain` and
 * `jss-usp-line-up-main`/`uspLineUpMain` exist in the migration bundle
 * (`libs/cms-sanity-schema/src/sections/LineUp.ts`) — Sitecore's
 * `jss-content-card-sm-compact-line-up-main` and `jss-testimonials-card-line-up-main` have no Sanity-side
 * field, so those candidate lists have no second entry and always resolve empty there.
 */
export const LINE_UP_SLOTS = {
  main: ['jss-line-up-main', 'lineUpMain'],
  usp: ['jss-usp-line-up-main', 'uspLineUpMain'],
  contentCardSmCompact: ['jss-content-card-sm-compact-line-up-main'],
  testimonialsCard: ['jss-testimonials-card-line-up-main'],
} as const;

/**
 * `ContentCard`'s nested placeholders. Only `jss-content-card-main`/`contentCardMain` exists in the
 * migration bundle (`libs/cms-sanity-schema/src/sections/ContentCard.ts`) — Sitecore's `jss-promo-box`
 * has no Sanity-side field on `ContentCard` itself (unlike `SplitView`, which does model it), so that
 * candidate list has no second entry and always resolves empty there.
 */
export const CONTENT_CARD_SLOTS = {
  promo: ['jss-promo-box'],
  main: ['jss-content-card-main', 'contentCardMain'],
} as const;

/**
 * `MainNavigation`'s nested placeholders — `libs/cms-sanity-schema/src/sections/MainNavigation.ts`'s
 * `logo`/`mainNavigationLeft`/`mainNavigationRight`/etc. On real content only `jss-logo` and
 * `jss-main-navigation-left` are populated (confirmed against a live page); the rest are wired for
 * whichever pages/sites do use them.
 */
export const MAIN_NAVIGATION_SLOTS = {
  headerLang: ['jss-header-lang', 'headerLang'],
  logo: ['jss-logo', 'logo'],
  breadcrumbs: ['jss-main-navigation-breadcrumbs', 'mainNavigationBreadcrumbs'],
  left: ['jss-main-navigation-left', 'mainNavigationLeft'],
  right: ['jss-main-navigation-right', 'mainNavigationRight'],
  secondary: ['jss-secondary-navigation', 'secondaryNavigation'],
  side: ['jss-side-navigation', 'sideNavigation'],
  topheader: ['jss-topheader', 'topheader'],
} as const;

/**
 * `Footer`'s nested placeholders — `libs/cms-sanity-schema/src/sections/Footer.ts`'s
 * `footerNavigation`/`subfooterLeft`/`subfooterRight`. On real content only `jss-subfooter-left` is
 * populated (confirmed against a live page); the rest are wired for whichever pages/sites do use them.
 */
export const FOOTER_SLOTS = {
  navigation: ['jss-footer-navigation', 'footerNavigation'],
  subfooterLeft: ['jss-subfooter-left', 'subfooterLeft'],
  subfooterRight: ['jss-subfooter-right', 'subfooterRight'],
} as const;

/**
 * `Centered`'s one nested placeholder. `Centered` carries no content of its own — it is a width
 * constraint (`colSpan`) plus an optional divider, wrapping whatever sits inside it.
 */
export const CENTERED_SLOTS = {
  main: ['jss-centered-main', 'centeredMain'],
} as const;

/**
 * `Carousel`'s one nested placeholder — its slides. Each slide is an ordinary block (typically a
 * `ContentCard`), so the carousel itself only positions them.
 */
export const CAROUSEL_SLOTS = {
  main: ['jss-carousel-main', 'carouselMain'],
} as const;

/** `GenericForm`'s one nested placeholder — its field blocks. */
export const GENERIC_FORM_SLOTS = {
  main: ['jss-form', 'form'],
} as const;

/**
 * `RevolutionForm`'s one nested placeholder — its field blocks. On Sanity they are siblings whose
 * `area` is the same literal name (`containerSlots.ts` in schema build 37).
 */
export const REVOLUTION_FORM_SLOTS = {
  main: ['jss-revolution-form'],
} as const;

/** `MegaMenu`'s nested placeholders — its divisions, plus an optional promo card column. */
export const MEGA_MENU_SLOTS = {
  divisions: ['jss-megamenu-divisions', 'megamenuDivisions'],
  cards: ['jss-megamenu-cards', 'megamenuCards'],
} as const;

/** `MegaMenuDivision`'s one nested placeholder — the cards shown beside its links. */
export const MEGA_MENU_DIVISION_SLOTS = {
  cards: ['jss-megamenu-division-cards', 'megamenuDivisionCards'],
} as const;

/**
 * `PaginatedContent`'s one nested placeholder. No Sanity-side field exists at all
 * (`libs/cms-sanity-schema/src/sections/PaginatedContent.ts` has no matching array field), so this
 * candidate list has no second entry and always resolves empty there.
 */
export const PAGINATED_CONTENT_SLOTS = {
  footer: ['jss-paginated-content-footer'],
} as const;

/**
 * `RegisterToNewsletterForm`'s three nested placeholders, shared with
 * `RevolutionRegisterToNewsletterForm` and `NewsletterFormIntegratedWithRevolution`. The migration bundle
 * declares no placeholder field for the first two (`libs/cms-sanity-schema/src/sections/
 * RegisterToNewsletterForm.ts` carries only `backButtonText`/`disclaimerContent`/`nextButtonText`/
 * `formFields`), so for them each candidate list resolves empty on the Sanity lane — the form still
 * submits, but its intro, success and error copy render there only once the schema carries them.
 * `NewsletterFormIntegratedWithRevolution`'s schema does declare these areas
 * (`libs/cms-sanity-schema/src/lib/containerSlots.ts`), so for that block they resolve on Sanity too.
 */
export const NEWSLETTER_FORM_SLOTS = {
  intro: ['jss-newsletter-form-intro'],
  success: ['jss-newsletter-form-success'],
  error: ['jss-newsletter-form-error'],
} as const;

/**
 * `MyEnvDisabledLandingPageCard`'s one nested placeholder — a `StoreButtons` block, the only allowed
 * child type (`libs/cms-sanity-schema/src/lib/containerSlots.ts`), already migrated.
 */
export const MY_ENV_DISABLED_LANDING_PAGE_CARD_SLOTS = {
  main: ['jss-my-env-blocked-main'],
} as const;

/**
 * `ButtonLineUp`'s one nested placeholder — its `ButtonLink` blocks. The block is field-less
 * (`libs/cms-sanity-schema/src/sections/ButtonLineUp.ts`), so its children reach the Sanity lane only as
 * flat siblings whose `area` is the literal Sitecore name (`libs/cms-sanity-schema/src/lib/containerSlots.ts`)
 * — one candidate resolves on both providers.
 */
export const BUTTON_LINE_UP_SLOTS = {
  buttons: ['jss-buttons'],
} as const;

/**
 * `NavigationView`'s two nested placeholders. The block is field-less
 * (`libs/cms-sanity-schema/src/sections/NavigationView.ts`), so its children reach the Sanity lane only
 * as flat siblings whose `area` is the literal Sitecore name (`libs/cms-sanity-schema/src/lib/containerSlots.ts`)
 * — one candidate each resolves on both providers.
 */
export const NAVIGATION_VIEW_SLOTS = {
  first: ['jss-navigation-view-first'],
  second: ['jss-navigation-view-second'],
} as const;

/**
 * `WarmthInterruptions`' one nested placeholder — its intro `TitleTextCTA`. Like `ButtonLineUp`, the
 * Sanity side has no placeholder field for it: the intro arrives as a flat sibling whose `area` is the
 * literal Sitecore name (`libs/cms-sanity-schema/src/lib/containerSlots.ts`), so one candidate resolves on
 * both providers.
 */
export const WARMTH_INTERRUPTIONS_SLOTS = {
  intro: ['jss-interruptions-intro'],
} as const;

/**
 * `ContentHubFilter`'s one nested placeholder — its introduction, a `TitleTextCTA` on acceptance. The
 * Sanity side has no placeholder field for it: the introduction arrives as a flat sibling whose `area` is
 * the literal Sitecore name (`libs/cms-sanity/src/slot-metadata.gen.ts`), so one candidate resolves on
 * both providers.
 */
export const CONTENT_HUB_FILTER_SLOTS = {
  introduction: ['jss-content-hub-filter-introduction'],
} as const;
